import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import {
  getV1CompaniesCompanyIdMaterialTypeahead,
  OrderUnitOfMeasure,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { uniq } from 'lodash-es';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import AutocompleteMaterialsTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteMaterialsTypeahead';
import AutocompleteOrderUnitOfMeasure from '~components/FormFields/CommonAutocompletes/AutocompleteOrderUnitOfMeasure';
import AutocompleteScopedCompaniesTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteScopedCompaniesTypeahead';
import SelectAllOption, {
  SelectAllState,
} from '~components/FormFields/Parts/SelectAllOption';
import AutocompleteField from '~components/FormFields/v2/Autocomplete/AutocompleteField';
import { AutocompleteFieldOption } from '~components/FormFields/v2/Autocomplete/types';
import NumberInput from '~components/FormFields/v2/NumberInput';
import TextInput from '~components/FormFields/v2/TextInput';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { EquipmentTypeItem } from '~hooks/useEquipment/models';
import { MaterialTypeahead } from '~hooks/useMaterials/models';
import { RemoveItemButton } from '~pages/Sales/sharedSales/RemoveItemButton';
import useVetaCenterConnectionQuery from '~queries/useVetaCenterConnectionQuery';
import reportRequestError from '~services/reportRequestError';
import { useStores } from '~store';
import { fetchAllApiPages } from '~utils/fetchAllPages';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  EquipmentTypeDto,
  EquipmentTypeDtoAsyncError,
  equipmentTypeSchema,
  getDriverClassificationOptions,
  setEquipmentTypeSchemaDefaultValues,
} from './equipmentTypeSchema';

export type EquipmentTypeFormHandler = {
  onSubmit: () => Promise<EquipmentTypeDto>;
};

interface EquipmentTypeFormProps {
  asyncErrors?: EquipmentTypeDtoAsyncError[];
  equipmentType?: EquipmentTypeItem;
}

const EquipmentTypeForm = forwardRef<EquipmentTypeFormHandler, EquipmentTypeFormProps>(
  function EquipmentTypeForm({ asyncErrors, equipmentType }, ref) {
    const { userStore } = useStores();

    const isEditing = Boolean(equipmentType);

    const form = useForm<EquipmentTypeDto>({
      resolver: yupResolver(equipmentTypeSchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues: setEquipmentTypeSchemaDefaultValues(userStore, equipmentType),
    });

    const selectedCompanyId = form.watch('company')?.value;
    const selectedMaterials = form.watch('materials');
    const [companyMaterialOptions, setCompanyMaterialOptions] = useState<
      AutocompleteFieldOption<MaterialTypeahead>[] | null
    >(null);
    const { isVetaCenterConnected } = useVetaCenterConnectionQuery({
      companyId: selectedCompanyId,
    });

    const additionalGrossCapacities = useFieldArray({
      control: form.control,
      name: 'additionalGrossCapacities',
    });
    const additionalGrossCapacitiesFields = form.watch('additionalGrossCapacities');

    const alreadyUsedUnitsOfMeasure = useMemo(() => {
      return uniq(
        additionalGrossCapacitiesFields.reduce((acc, field) => {
          if (field.unitOfMeasure?.value) {
            acc.push(field.unitOfMeasure.value as OrderUnitOfMeasure);
          }

          return acc;
        }, [] as OrderUnitOfMeasure[]),
      );
    }, [JSON.stringify(additionalGrossCapacitiesFields)]);

    const driverClassificationOptions = useMemo(getDriverClassificationOptions, []);

    useEffect(() => {
      if (!isVetaCenterConnected) {
        form.resetField('driverClassification');
      }
    }, [isVetaCenterConnected, form.resetField]);

    const previousCompanyIdRef = useRef(selectedCompanyId);
    useEffect(() => {
      if (selectedCompanyId === previousCompanyIdRef.current) {
        return;
      }
      previousCompanyIdRef.current = selectedCompanyId;

      const hasSelectedMaterials = form.getValues('materials').length > 0;
      if (hasSelectedMaterials) {
        form.setValue('materials', [], { shouldDirty: true });
      }
    }, [form.getValues, form.setValue, selectedCompanyId]);

    // Select all needs the company's whole list to know when every material is picked
    useEffect(() => {
      setCompanyMaterialOptions(null);

      if (!selectedCompanyId) {
        return;
      }

      const controller = new AbortController();

      const loadCompanyMaterials = async () => {
        try {
          const companyMaterials = await fetchAllApiPages(
            (query, signal) =>
              getV1CompaniesCompanyIdMaterialTypeahead({
                path: { 'company-id': selectedCompanyId },
                query,
                signal,
              }),
            MaterialTypeahead.parse,
            { signal: controller.signal },
          );

          setCompanyMaterialOptions(
            companyMaterials.map((material) => ({
              _entity: material,
              label: material.name,
              value: material.id,
            })),
          );
        } catch (error) {
          if (controller.signal.aborted) {
            return;
          }

          reportRequestError(error, t('error_messages.materials.failed_to_fetch'));
        }
      };

      void loadCompanyMaterials();

      return () => controller.abort();
    }, [selectedCompanyId]);

    const selectedMaterialIds = new Set(selectedMaterials.map(({ value }) => value));
    const everyCompanyMaterialIsSelected = Boolean(
      companyMaterialOptions?.length &&
      companyMaterialOptions.every(({ value }) => selectedMaterialIds.has(value)),
    );
    const selectAllMaterialsState: SelectAllState = everyCompanyMaterialIsSelected
      ? 'all'
      : selectedMaterials.length
        ? 'some'
        : 'none';

    const toggleAllMaterials = () => {
      if (!companyMaterialOptions) {
        return;
      }

      const unselectedMaterialOptions = companyMaterialOptions.filter(
        ({ value }) => !selectedMaterialIds.has(value),
      );
      const nextMaterials = everyCompanyMaterialIsSelected
        ? []
        : [...selectedMaterials, ...unselectedMaterialOptions];

      form.setValue('materials', nextMaterials, { shouldDirty: true });
    };

    const allowedUnits = userStore.userCompany?.allowedOrderUnitsOfMeasure ?? [];

    const canAddAdditionalGrossCapacity = useMemo(() => {
      const maxRowsCount =
        allowedUnits.length > 0
          ? allowedUnits.length
          : Object.values(OrderUnitOfMeasure).length;
      const addedRowsCount = additionalGrossCapacitiesFields.length;

      return maxRowsCount - addedRowsCount > 0;
    }, [additionalGrossCapacitiesFields.length, allowedUnits.length]);

    useImperativeHandle(
      ref,
      () => ({
        onSubmit() {
          return new Promise((resolve) => {
            form.handleSubmit((data) => {
              resolve(data);
            })();
          });
        },
      }),
      [form.handleSubmit],
    );

    useEffect(() => {
      if (asyncErrors) {
        for (const error of asyncErrors) {
          form.setError(error.field as keyof EquipmentTypeDto, {
            message: error.message,
          });
        }
      }

      return () => {
        form.clearErrors();
      };
    }, [asyncErrors]);

    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
        <Box sx={{ gridColumn: 'span 2' }}>
          <AutocompleteScopedCompaniesTypeahead
            companyId={getEffectiveUserCompanyId(userStore)}
            control={form.control}
            disableClearable
            isDisabled={isEditing}
            isRequired
            label={t('form_fields.company')}
            name="company"
          />
        </Box>

        <Box>
          <TextInput
            control={form.control}
            isRequired
            label={t('form_fields.name')}
            name="name"
          />
        </Box>

        <Box>
          <TextInput
            control={form.control}
            label={t('form_fields.external_id')}
            name="externalId"
          />
        </Box>

        <Box>
          <TextInput
            control={form.control}
            label={t('administration.equipment.accounting_id')}
            name="accountingId"
          />
        </Box>
        <Box />

        {isVetaCenterConnected && (
          <Box sx={{ gridColumn: 'span 2' }}>
            <AutocompleteField
              control={form.control}
              label={t('administration.equipment_type.driver_classification')}
              name="driverClassification"
              options={driverClassificationOptions}
            />
          </Box>
        )}

        <Box>
          <NumberInput
            control={form.control}
            label={t('administration.equipment_type.default_gross_capacity')}
            name="grossCapacity"
          />
        </Box>

        <Box>
          <AutocompleteOrderUnitOfMeasure
            control={form.control}
            label={t('form_fields.unit')}
            name="grossCapacityUnitOfMeasure"
          />
        </Box>

        <Box sx={{ gridColumn: 'span 2' }}>
          <AutocompleteMaterialsTypeahead
            companyId={selectedCompanyId}
            control={form.control}
            isMultiple
            label={t('administration.equipment_type.allowed_materials')}
            listboxHeader={
              <SelectAllOption
                isDisabled={!companyMaterialOptions?.length}
                label={t('administration.equipment_type.select_all_materials')}
                onClick={toggleAllMaterials}
                state={selectAllMaterialsState}
              />
            }
            name="materials"
          />
        </Box>

        <Box sx={{ gridColumn: 'span 2' }}>
          <Divider />
        </Box>

        {additionalGrossCapacities.fields.map((field, index) => {
          return (
            <Box
              key={field.id}
              sx={{
                display: 'grid',
                gap: 2,
                gridColumn: 'span 2',
                gridTemplateColumns: 'repeat(2, 1fr) auto',
              }}
            >
              <Box>
                <NumberInput
                  control={form.control}
                  label={t('form_fields.gross_capacity')}
                  name={`additionalGrossCapacities.${index}.capacity`}
                />
              </Box>

              <Box>
                <AutocompleteOrderUnitOfMeasure
                  control={form.control}
                  filterOptions={alreadyUsedUnitsOfMeasure}
                  label={t('form_fields.unit')}
                  name={`additionalGrossCapacities.${index}.unitOfMeasure`}
                />
              </Box>

              <Box>
                <RemoveItemButton
                  action={() => additionalGrossCapacities.remove(index)}
                  sx={{ cursor: 'pointer', mt: '27px', width: '100%' }}
                />
              </Box>
            </Box>
          );
        })}

        {canAddAdditionalGrossCapacity && (
          <Box sx={{ gridColumn: 'span 2' }}>
            <PseudoLink
              action={() => {
                additionalGrossCapacities.append({
                  capacity: '' as unknown as number,
                  unitOfMeasure:
                    null as unknown as AutocompleteFieldOption<OrderUnitOfMeasure>,
                });
              }}
              title={t('administration.equipment_type.add_gross_capacity')}
              sx={{ display: 'inline-block' }}
            />
          </Box>
        )}
      </Box>
    );
  },
);

export default EquipmentTypeForm;
